import Data from '@/data'
import { PassiveSkillDefinition } from '@/data/types'
import { NodeState, TreeViewStatePassives, radiusJewel } from '@/logic/tree'
import { TreeDataProvider } from '@/logic/treeData'
import { PlayerClass } from '@/types/profile'
import { notNull } from '@/utils/validation'
import { BundleData, getBundle, imagePath, imagePaths, loadFont, skillIconPath } from './assets'
import { InteractAscendancy, InteractAscendancyButton, frameColor, lineState } from './renderItems'
import { Layer, RenderItem, Renderer } from './renderer'
import { textRenderer } from './text'

function nodeColor(state: NodeState) {
  switch (state) {
    case 'add':
    case 'removeHistory':
      return { r: 0.8, g: 0.8, b: 0.8, a: 1.0, desaturate: 0.2 }
    case undefined:
      return { r: 0.6, g: 0.6, b: 0.6, a: 1.0, desaturate: 0.4 }
  }
}

const treeBackground = imagePath('Art/2DArt/UIImages/Common/Background2')

const groupBackgrounds = imagePaths({
  small: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenGroupBackgroundSmall',
  medium: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenGroupBackgroundMedium',
  large: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenGroupBackgroundLargeHalf'
})
const groupBackgroundsProxy = imagePaths({
  small: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenGroupBackgroundSmallAlt',
  medium: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenGroupBackgroundMediumAlt',
  large: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenGroupBackgroundLargeHalfAlt'
})

const frameNormal = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenPassiveFrameNormal',
  active: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenPassiveFrameActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenPassiveFrameCanAllocate'
})
const frameAscendancy = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyFrameSmallNormal',
  active: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyFrameSmallAllocated',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyFrameSmallCanAllocate'
})
const frameNotable = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenNotableFrameNormal',
  active: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenNotableFrameActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenNotableFrameCanAllocate'
})
const frameNotableAscendancy = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyFrameLargeNormal',
  active: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyFrameLargeAllocated',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyFrameLargeCanAllocate'
})
const frameKeystone = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenKeystoneFrameNormal',
  active: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenKeystoneFrameActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenKeystoneFrameCanAllocate'
})
const frameJewel = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelSocketNormal',
  active: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelSocketActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelSocketCanAllocate'
})
const frameJewelLarge = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelSocketAltNormal',
  active: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelSocketAltActive',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelSocketAltCanAllocate'
})
const frameJewelCircle = {
  Small: imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/JewelSocketClusterAltNormal1Small',
    canAllocate: 'Art/2DArt/UIImages/InGame/JewelSocketClusterAltCanAllocate1Small'
  }),
  Medium: imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/JewelSocketClusterAltNormal1Medium',
    canAllocate: 'Art/2DArt/UIImages/InGame/JewelSocketClusterAltCanAllocate1Medium'
  }),
  Large: imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/JewelSocketClusterAltNormal1Large',
    canAllocate: 'Art/2DArt/UIImages/InGame/JewelSocketClusterAltCanAllocate1Large'
  })
}
const frameMastery = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveMastery/PassiveMasteryButtonInactiveCantAllocate',
  canAllocate: 'Art/2DArt/UIImages/InGame/PassiveMastery/PassiveMasteryButtonInactiveDefault'
})

const ascendancyButtons = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyButton',
  hover: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyButtonHighlight',
  pressed: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyButtonPressed'
})

const lineTextures = imagePaths({
  normal: 'Art/2DArt/PassiveTree/PassiveSkillScreenCurvesNormalTogether',
  active: 'Art/2DArt/PassiveTree/PassiveSkillScreenCurvesActiveTogether',
  intermediate: 'Art/2DArt/PassiveTree/PassiveSkillScreenCurvesIntermediateTogether',
  ornament: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenOrnament1'
})
const lineColors = {
  normal: 0x131b1e,
  active: 0x74a3b9,
  intermediate: 0x49676e
}

const startInactive = imagePath('Art/2DArt/UIImages/InGame/PassiveSkillScreenStartNodeBackgroundInactive')
const startActive = (id: PlayerClass) =>
  imagePath(`Art/2DArt/UIImages/InGame/Classes/${id}/PassiveSkillScreenStartNodeBackground`)

const ascendancyRoot = imagePath('Art/2DArt/UIImages/InGame/PassiveSkillScreenAscendancyMiddle')

const jewelRadius = imagePaths({
  normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelCircle1',
  outer: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelCircle1',
  inverse: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenJewelCircle1inverse'
})
const jewelRadiusSpecial = [
  imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenVaalJewelCircle1',
    outer: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenVaalJewelCircle2'
  }),
  imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenKaruiJewelCircle1',
    outer: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenKaruiJewelCircle2'
  }),
  imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenMarakethJewelCircle1',
    outer: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenMarakethJewelCircle2'
  }),
  imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenTemplarJewelCircle1',
    outer: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenTemplarJewelCircle2'
  }),
  imagePaths({
    normal: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenEternalEmpireJewelCircle1',
    outer: 'Art/2DArt/UIImages/InGame/PassiveSkillScreenEternalEmpireJewelCircle2'
  })
]

function nodeSize(skill: PassiveSkillDefinition) {
  if (skill.is_ascendancy_root) {
    return 10
  } else if (skill.is_jewel_socket) {
    return 30
  } else if (skill.is_notable) {
    return 48
  } else if (skill.is_keystone) {
    return 84
  } else if (skill.is_just_icon) {
    return 0
  } else {
    return 26
  }
}

export function renderPassiveTree(data: TreeDataProvider, ascendancyButton?: 'hover' | 'pressed') {
  const items: RenderItem[] = []
  const tree = data.state as TreeViewStatePassives

  if (!tree.ascendancyOnly && !tree.hideBackground) {
    items.push({
      layer: Layer.BACKGROUND,
      image: treeBackground,
      type: 'background'
    })
  }

  for (const id of Object.keys(data.tree.groups)) {
    const group = data.group(Number(id))
    if (!group) continue
    if (!data.showAscendancy(group.ascendancy)) continue
    const backgrounds = group.proxy ? groupBackgroundsProxy : groupBackgrounds
    if (group.bg === 1) {
      items.push({
        layer: Layer.GROUP,
        image: backgrounds.small,
        type: 'image',
        x: group.x,
        y: group.y,
        centered: true
      })
    } else if (group.bg === 2) {
      items.push({
        layer: Layer.GROUP,
        image: backgrounds.medium,
        type: 'image',
        x: group.x,
        y: group.y,
        centered: true
      })
    } else if (group.bg === 3) {
      items.push({
        layer: Layer.GROUP,
        image: backgrounds.large,
        type: 'twohalf',
        x: group.x,
        y: group.y
      })
    }
  }

  for (const id of data.allNodes()) {
    const node = data.node(id)!
    if (!data.showAscendancy(node.skill.ascendancy)) continue

    const skill = data.nodeSkill(id)!

    const layerDelta = skill.ascendancy ? Layer.ASCENDANCY : 0
    if (skill.is_ascendancy_root) {
      items.push({
        layer: Layer.NODE_FRAME + layerDelta,
        image: ascendancyRoot,
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true
      })
      items.push({
        layer: Layer.ASCENDANCY,
        image:
          data.state.version === 326 ? `ascendancy_${skill.ascendancy!}_league` : `ascendancy_${skill.ascendancy!}`,
        type: 'image',
        interactKey: InteractAscendancy,
        x: node.x,
        y: node.y,
        centered: true
      })
    } else if (skill.is_jewel_socket) {
      const jewelSlot = Data.jewel_slots[node.skill_id]
      const frames = jewelSlot?.size ? frameJewelLarge : frameJewel
      items.push({
        layer: Layer.NODE_FRAME + layerDelta,
        image: node.state ? frames.active : frames.normal,
        color: frameColor(node.state),
        type: 'image',
        interactKey: id,
        x: node.x,
        y: node.y,
        centered: true
      })
      if (node.jewel) {
        const base = Data.items[node.jewel.base]
        const icon = jewelSlot?.size ? base?.visual_identity.socketed_image_large : base?.visual_identity.socketed_image
        if (icon) {
          items.push({
            layer: Layer.NODE_JEWEL + layerDelta,
            image: imagePath(icon),
            type: 'image',
            x: node.x,
            y: node.y,
            centered: true
          })
        }

        if (node.active) {
          const radius = radiusJewel(data, id)
          if (radius) {
            const tex = (radius.treeVersion && jewelRadiusSpecial[radius.treeVersion - 1]) || jewelRadius
            items.push({
              layer: Layer.JEWEL_RADIUS1,
              image: tex.normal,
              type: 'image',
              x: radius.x,
              y: radius.y,
              centered: true,
              size: radius.maxRadius * 2
            })
            items.push({
              layer: Layer.JEWEL_RADIUS2,
              image: tex.outer,
              type: 'image',
              x: radius.x,
              y: radius.y,
              centered: true,
              angle: 180,
              size: radius.maxRadius * 2
            })
            if (radius.minRadius) {
              items.push({
                layer: Layer.JEWEL_RADIUS1,
                image: jewelRadius.inverse,
                type: 'image',
                x: radius.x,
                y: radius.y,
                centered: true,
                size: radius.minRadius * 2.125
              })
              items.push({
                layer: Layer.JEWEL_RADIUS2,
                image: jewelRadius.inverse,
                type: 'image',
                x: radius.x,
                y: radius.y,
                centered: true,
                angle: 180,
                size: radius.minRadius * 2.125
              })
            }
          }
        }
      } else if (jewelSlot?.size) {
        const frames = frameJewelCircle[jewelSlot.size]
        items.push({
          layer: Layer.NODE_JEWEL + layerDelta,
          image: node.state ? frames.canAllocate : frames.normal,
          type: 'image',
          x: node.x,
          y: node.y,
          centered: true
        })
      }
      //TODO: jewel icon and radius go here
    } else if (skill.starting_node) {
      items.push({
        layer: Layer.NODE_FRAME + layerDelta,
        image: skill.starting_node === tree.charClass ? startActive(skill.starting_node) : startInactive,
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true
      })
    } else if (skill.mastery) {
      if (node.active) {
        items.push({
          layer: Layer.MASTERY_BACKGROUND + layerDelta,
          image: imagePath(skill.mastery.active_image),
          type: 'image',
          x: node.x,
          y: node.y,
          centered: true
        })
      }
      if (!node.state || node.state === 'add' || node.state === 'addHistory') {
        items.push({
          layer: Layer.MASTERY_FRAME + layerDelta,
          image: node.state ? frameMastery.canAllocate : frameMastery.normal,
          color: node.state ? { r: 0.0, g: 2.0, b: 0.0, a: 1.0 } : undefined,
          type: 'image',
          x: node.x,
          y: node.y,
          centered: true
        })
      }
      items.push({
        layer: Layer.MASTERY_ICON + layerDelta,
        image: imagePath(
          node.state === 'active' || node.state === 'remove' || node.state === 'removeHistory'
            ? skill.mastery.active_icon
            : skill.mastery.inactive_icon
        ),
        interactKey: id,
        color:
          !node.state || node.state === 'add'
            ? { r: 1.0, g: 1.0, b: 1.0, a: 0.5 }
            : node.state === 'remove' || node.state === 'removeHistory'
            ? { r: 1.5, g: 0.2, b: 0.2, a: 1.0 }
            : undefined,
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true
      })
    } else if (skill.is_just_icon) {
      items.push({
        layer: Layer.MASTERY_ICON + layerDelta,
        image: skillIconPath(skill.icon),
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true
      })
    } else {
      const tattoo = node.tattoo && Data.tattoos[node.tattoo]
      if (tattoo && tattoo.background) {
        items.push({
          layer: Layer.MASTERY_BACKGROUND + layerDelta,
          image: imagePath(tattoo.background),
          type: 'image',
          x: node.x,
          y: node.y,
          centered: true
        })
      }
      items.push({
        layer: Layer.NODE_ICON + layerDelta,
        image: skillIconPath(skill.icon),
        color: nodeColor(node.state),
        type: 'image',
        x: node.x,
        y: node.y,
        centered: true,
        size: skill.is_notable ? 98 : skill.is_keystone ? 128 : 64
      })
      const frames = skill.is_notable
        ? skill.ascendancy
          ? frameNotableAscendancy
          : frameNotable
        : skill.is_keystone
        ? frameKeystone
        : skill.ascendancy
        ? frameAscendancy
        : frameNormal
      items.push({
        layer: Layer.NODE_FRAME + layerDelta,
        image: node.state ? frames.active : frames.normal,
        color: frameColor(node.state),
        type: 'image',
        interactKey: id,
        x: node.x,
        y: node.y,
        centered: true
      })
    }

    for (const id2 of node.connections) {
      if (id2 <= id) continue
      const node2 = data.node(id2)
      if (!node2) continue
      const skill2 = node2.skill
      if (skill.ascendancy !== skill2.ascendancy) continue
      if (skill.starting_node || skill2.starting_node) continue
      if (skill.is_just_icon || skill2.is_just_icon) continue

      const state = lineState(node.state, node2.state)
      const flatColor = state ? lineColors.active : lineColors.normal
      if (node.group === node2.group && node.radius === node2.radius) {
        items.push({
          layer: Layer.LINE + layerDelta,
          image: state ? lineTextures.active : lineTextures.normal,
          color: frameColor(state),
          flatColor,
          type: 'arc',
          x: node.group.x,
          y: node.group.y,
          r: node.radius,
          a1: node.arc,
          a2: node2.arc
        })
      } else {
        items.push({
          layer: Layer.LINE + layerDelta,
          image: state ? lineTextures.active : lineTextures.normal,
          color: frameColor(state),
          flatColor,
          type: 'line',
          x1: node.x,
          y1: node.y,
          x2: node2.x,
          y2: node2.y
        })
        const dx = node2.x - node.x
        const dy = node2.y - node.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        const angle = Math.atan2(dy, dx)
        const r1 = nodeSize(skill) + 24
        const r2 = nodeSize(skill2) + 24
        items.push({
          layer: Layer.LINE_ORNAMENT + layerDelta,
          image: lineTextures.ornament,
          color: frameColor(state),
          type: 'image',
          x: node.x + (dx * r1) / dist,
          y: node.y + (dy * r1) / dist,
          angle: angle,
          centered: true
        })
        items.push({
          layer: Layer.LINE_ORNAMENT + layerDelta,
          image: lineTextures.ornament,
          color: frameColor(state),
          type: 'image',
          x: node2.x - (dx * r2) / dist,
          y: node2.y - (dy * r2) / dist,
          angle: angle + Math.PI,
          centered: true
        })
      }
    }
  }

  if (tree.highlight) {
    for (const id of tree.highlight) {
      const node = data.node(id)
      if (!node || !data.showAscendancy(node.skill.ascendancy)) continue

      let size = node.skill.mastery
        ? 224
        : node.skill.is_keystone
        ? 256
        : node.skill.is_notable || node.skill.is_jewel_socket
        ? 192
        : 128

      items.push({
        layer: Layer.NODE_HIGHLIGHT + (node.skill.ascendancy ? Layer.ASCENDANCY : 0),
        image: treeBackground, // neutral image that's always loaded
        type: 'circle',
        x: node.x,
        y: node.y,
        r: size / 2,
        color: { r: 0, g: 1, b: 0, a: 1 }
      })
    }
  }

  if (!tree.ascendancyOnly) {
    const chr = Data.characters[tree.charClass]

    const start = data.tree.characterRoot[tree.charClass]
    const root = data.node(start)!

    const ascendancy = tree.ascendancy ? chr.ascendancies[tree.ascendancy] : undefined
    if (ascendancy) {
      const dist = Math.sqrt(root.x * root.x + root.y * root.y)
      let dirX = 0.0
      let dirY = 1.0
      if (dist > 10.0) {
        dirX = -root.x / dist
        dirY = -root.y / dist
      }
      const angle = Math.atan2(dirY, dirX)
      items.push({
        layer: Layer.ASCENDANCY_BUTTON,
        image: ascendancyButtons[ascendancyButton ?? 'normal'],
        type: 'image',
        interactKey: InteractAscendancyButton,
        x: root.x + 270 * Math.cos(angle),
        y: root.y + 270 * Math.sin(angle),
        angle: angle - Math.PI / 2,
        centered: true
      })
    }

    const illustration =
      (data.state.version === 326 ? ascendancy?.league_illustration : ascendancy?.illustration) ?? chr.illustration
    if (illustration) {
      items.push({
        layer: Layer.ARTWORK,
        image: imagePath(illustration.image),
        type: 'image',
        x: illustration.x + root.x,
        y: illustration.y + root.y,
        centered: false
      })
    }
  }

  return items
}

async function renderAscendancyText(
  charClass: PlayerClass,
  ascendancy: string,
  version: number
): Promise<BundleData | undefined> {
  const data = Data.characters[charClass]?.ascendancies[ascendancy]
  if (!data) return

  const common_bundle = await getBundle('passive_tree_common')
  const bundle = await getBundle(`passive_tree_${charClass.toLowerCase()}`)
  await loadFont('FontinItalic')

  async function render(background: string, flavour_text: string, key: string) {
    const entry = bundle.find((e) => e.index[background]) ?? common_bundle.find((e) => e.index[background])
    if (!entry) return
    const rect = entry.index[background]
    const canvas = document.createElement('canvas')
    let size = 128
    while (size < rect.w || size < rect.h) {
      size *= 2
    }
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(entry.image, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h)
    ctx.font = '24px FontinItalic'
    ctx.textAlign = 'start'
    ctx.textBaseline = 'top'
    ctx.lineWidth = 1
    ctx.strokeStyle = '#000'
    ctx.fillStyle = `rgb(${data.flavour_colour})`
    let [x, y] = data.flavour_rect.split(',').map((p) => parseInt(p))
    x >>= 1
    y >>= 1
    for (const line of flavour_text.replace(/\r/g, '').split('\n')) {
      await textRenderer.load(line)
      textRenderer.draw(ctx, line, x, y)
      y += 28
    }
    const image = window.createImageBitmap ? await window.createImageBitmap(canvas) : canvas
    return {
      image,
      index: {
        [key]: {
          x: 0,
          y: 0,
          w: rect.w,
          h: rect.h,
          scale: 2
        }
      }
    }
  }

  return [
    await render(data.background, data.flavour_text, `ascendancy_${ascendancy}`),
    await render(data.league_background, data.league_flavour_text, `ascendancy_${ascendancy}_league`)
  ].filter(notNull)
}

export function loadPassiveTree(tree: TreeViewStatePassives, renderer: Renderer) {
  const bundles = ['passive_tree_common']
  if (!tree.ascendancyOnly) bundles.push('passive_tree_icons')
  bundles.push(`passive_tree_${tree.charClass.toLowerCase()}`)
  if (
    bundles.every((name) => renderer.loaded.has(name)) &&
    (!tree.ascendancy || renderer.loaded.has(`ascendancy_${tree.ascendancy}`))
  )
    return undefined
  const promises: Promise<any>[] = bundles.map((name) =>
    getBundle(name).then((bundle) => renderer.loadBundle(name, bundle))
  )
  if (tree.ascendancy) {
    promises.push(
      renderAscendancyText(tree.charClass, tree.ascendancy, tree.version).then((img) => {
        if (img) renderer.loadBundle(`ascendancy_${tree.ascendancy}`, img)
      })
    )
  }
  return Promise.all(promises)
}
